import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/api/client";
import TopBar from "@/components/TopBar";
import PushEnableBanner from "@/components/PushEnableBanner";
import BannerCarousel from "@/components/BannerCarousel";
import CategoryGrid, { CATEGORY_TILES } from "@/components/CategoryGrid";
import CategoryPicker, { loadLane, saveLane } from "@/components/CategoryPicker";
import useWebSocket from "@/hooks/useWebSocket";
import { Store, MapPin, Clock, Star, ChevronLeft, Search, HelpCircle, RefreshCw } from "lucide-react";
import HowToUseModal, { shouldShowHowTo } from "@/components/HowToUseModal";
import { useAuth } from "@/contexts/AuthContext";

export default function RestaurantsList() {
  const { user } = useAuth();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [lane, setLane] = useState(loadLane());
  const [showPicker, setShowPicker] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (shouldShowHowTo()) setShowHowTo(true);
    // First visit after login for authenticated customers: prompt the two-lane
    // chooser. Guest browsing skips this so they land on shops immediately.
    if (user && user.role === "customer" && !loadLane()) {
      setShowPicker(true);
    }
  }, [user]);

  // When the user picks a lane, seed the shop-type filter accordingly.
  const applyLane = (nextLane) => {
    setLane(nextLane);
    setShowPicker(false);
    if (nextLane === "restaurants") setTypeFilter("restaurant");
    else if (nextLane === "shops" && typeFilter === "restaurant") setTypeFilter("");
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/restaurants");
      setRestaurants(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  useWebSocket({
    onMessage: (msg) => {
      if (msg.event === "restaurant.updated" && msg.data && "owner_online" in msg.data) {
        setRestaurants((prev) => prev.map((r) => (
          r.id === msg.data.id ? {
            ...r,
            owner_online: msg.data.owner_online,
            manual_closed: msg.data.manual_closed ?? r.manual_closed,
            manual_closed_reason: msg.data.manual_closed_reason ?? null,
          } : r
        )));
        return;
      }
      if (msg.event?.startsWith("restaurant.")) load();
    },
  });

  const restaurantTypes = new Set(["restaurant"]);
  const filtered = restaurants
    .filter((r) => {
      const t = r.shop_type || "restaurant";
      // Lane pre-filter: restaurants lane keeps only restaurants; shops lane
      // excludes restaurants. "all" (or unset) passes everything.
      if (lane === "restaurants" && !restaurantTypes.has(t)) return false;
      if (lane === "shops" && restaurantTypes.has(t)) return false;
      // Fine-grained tile filter.
      if (typeFilter && t !== typeFilter) return false;
      return q === "" || r.name.includes(q) || (r.cuisine || "").includes(q) || (r.city || "").includes(q);
    })
    .sort((a, b) => {
      const aOpen = a.owner_online ? 1 : 0;
      const bOpen = b.owner_online ? 1 : 0;
      if (aOpen !== bOpen) return bOpen - aOpen;
      return 0;
    });

  // Visible tiles depend on the lane: restaurants lane hides non-food tiles,
  // shops lane hides the restaurants tile. "all"/unset shows everything.
  const visibleTiles = CATEGORY_TILES.filter((t) => {
    if (lane === "restaurants") return t.value === "restaurant";
    if (lane === "shops") return t.value !== "restaurant";
    return true;
  });

  const laneLabel = lane === "restaurants" ? "المطاعم" : lane === "shops" ? "متاجر ومحلات" : "كل المحلات";

  return (
    <div className="min-h-screen bg-[var(--background)]">
      <TopBar/>
      <PushEnableBanner/>

      {/* Promo banner carousel */}
      <BannerCarousel/>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-4">
        {/* Lane pill + change */}
        {lane && (
          <div className="flex items-center justify-between mb-3" data-testid="lane-summary">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-light text-primary-brand text-xs font-black">
              <Store size={12}/> {laneLabel}
            </div>
            <button onClick={() => setShowPicker(true)}
              className="text-xs font-bold text-primary-brand hover:underline flex items-center gap-1"
              data-testid="btn-change-lane">
              <RefreshCw size={11}/> غيّر التصنيف
            </button>
          </div>
        )}

        {/* Big circular category grid */}
        <CategoryGridScoped tiles={visibleTiles} value={typeFilter} onChange={setTypeFilter} showAll={!lane || lane === "all"}/>
      </div>

      {/* Search */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-5">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]"/>
            <input
              placeholder="ابحث عن محل أو تخصص..."
              value={q} onChange={(e) => setQ(e.target.value)}
              className="w-full pr-11 pl-4 py-3 rounded-2xl border border-[var(--border)] bg-white focus:border-primary-brand focus:outline-none"
              data-testid="restaurants-search"
            />
          </div>
          <button onClick={() => setShowHowTo(true)}
                  className="shrink-0 flex items-center gap-1.5 px-3 rounded-2xl border border-[var(--border)] bg-white hover:bg-blue-50 hover:border-primary-brand text-primary-brand font-black text-sm transition"
                  title="طريقة استخدام التطبيق"
                  data-testid="open-howto">
            <HelpCircle size={16}/>
            <span className="hidden sm:inline">كيف أستخدم؟</span>
          </button>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-lg font-black flex items-center gap-1" style={{fontFamily:'Cairo'}}>
            <span className="text-yellow-500">🌟</span> اختيارات مميزة لك
          </h2>
          <span className="text-xs text-[var(--text-secondary)]">{filtered.length} محل</span>
        </div>
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_,i)=><div key={i} className="h-64 rounded-2xl shimmer"/>)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-[var(--text-secondary)]">لا توجد محلات مطابقة</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((r) => (
              <Link
                key={r.id}
                to={`/r/${r.slug || r.id}`}
                className={`group bg-white rounded-3xl talabya-shadow-soft overflow-hidden border border-[var(--border)] hover:-translate-y-1 transition-all duration-300 animate-fade-up ${!r.owner_online ? "opacity-70" : ""}`}
                data-testid={`restaurant-card-${r.id}`}>
                <div className="relative h-48 overflow-hidden bg-[var(--surface-alt)]">
                  {r.image
                    ? <img src={r.image} alt={r.name} className={`w-full h-full object-cover group-hover:scale-105 transition ${!r.owner_online ? "grayscale" : ""}`}/>
                    : <div className="w-full h-full flex items-center justify-center text-4xl">🍽</div>}
                  {r.cuisine && (
                    <div className="absolute top-3 right-3 bg-white/95 backdrop-blur text-primary-brand text-xs font-bold px-3 py-1 rounded-full">
                      {r.cuisine}
                    </div>
                  )}
                  <div
                    className={`absolute top-3 left-3 backdrop-blur text-white text-xs font-black px-3 py-1 rounded-full flex items-center gap-1 shadow-sm ${r.owner_online ? "bg-emerald-500/95" : "bg-slate-500/95"}`}
                    data-testid={`shop-status-${r.id}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${r.owner_online ? "bg-white animate-pulse-dot" : "bg-white/70"}`}/>
                    {r.owner_online
                      ? "مفتوح"
                      : (r.manual_closed && r.manual_closed_reason ? `مغلق · ${r.manual_closed_reason}` : "مغلق")}
                  </div>
                  <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/50 to-transparent"/>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-black text-lg" style={{fontFamily:'Cairo'}}>{r.name}</h3>
                    <div className="flex items-center gap-0.5 text-xs bg-primary-light text-primary-brand px-2 py-1 rounded-full font-bold">
                      <Star size={11} fill="currentColor"/> جديد
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-[var(--text-secondary)] mb-1">
                    <MapPin size={12}/> {r.address}
                    {(r.address || (r.lat && r.lng)) && (
                      <a
                        href={
                          r.lat && r.lng
                            ? `https://www.google.com/maps/dir/?api=1&destination=${r.lat},${r.lng}`
                            : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((r.city ? r.city + " " : "") + (r.address || r.name))}`
                        }
                        target="_blank" rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="mr-auto text-primary-brand hover:underline font-bold text-[11px] flex items-center gap-0.5"
                        data-testid={`map-link-${r.id}`}
                        title="فتح الموقع في خرائط جوجل"
                      >
                        🗺️ الموقع
                      </a>
                    )}
                  </div>
                  {r.hours && (
                    <div className="flex items-center gap-1 text-xs text-[var(--text-secondary)]">
                      <Clock size={12}/> {r.hours}
                    </div>
                  )}
                  <div className="mt-3 pt-3 border-t border-[var(--border)] flex items-center justify-between">
                    <div className="text-xs text-[var(--text-secondary)]">توصيل خلال 30 دقيقة</div>
                    <div className="text-primary-brand font-bold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                      اطلب الآن <ChevronLeft size={14}/>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {showHowTo && <HowToUseModal onClose={() => setShowHowTo(false)}/>}
      {showPicker && <CategoryPicker onPick={applyLane} onClose={() => setShowPicker(false)}/>}
    </div>
  );
}

// Local wrapper around CategoryGrid that lets us optionally hide the "الكل"
// tile when a lane already scopes the selection.
function CategoryGridScoped({ tiles, value, onChange, showAll }) {
  return (
    <div className="grid grid-cols-4 sm:grid-cols-6 gap-3" data-testid="category-grid">
      {showAll && (
        <TileBtn
          active={!value}
          label="الكل"
          Icon={require("lucide-react").Grid3x3}
          gradient="from-blue-600 to-blue-800"
          testId="cat-tile-all"
          onClick={() => onChange("")}
        />
      )}
      {tiles.map((c) => {
        const Icon = c.icon;
        return (
          <TileBtn key={c.value} active={value === c.value} label={c.label} Icon={Icon} gradient={c.gradient}
                   testId={`cat-tile-${c.value}`} onClick={() => onChange(c.value === value ? "" : c.value)}/>
        );
      })}
    </div>
  );
}

function TileBtn({ active, label, Icon, gradient, testId, onClick }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className="group flex flex-col items-center gap-1.5 transition-transform hover:-translate-y-0.5 focus:outline-none">
      <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center text-white talabya-shadow-soft bg-gradient-to-br ${gradient} ${active ? "ring-4 ring-primary-brand ring-offset-2 ring-offset-[var(--background)]" : ""} group-hover:scale-105 transition-transform`}>
        <Icon size={26} strokeWidth={2.2}/>
      </div>
      <div className={`text-[11px] sm:text-xs font-black text-center max-w-[80px] leading-tight ${active ? "text-primary-brand" : "text-[var(--text-primary)]"}`} style={{fontFamily:"Cairo"}}>
        {label}
      </div>
    </button>
  );
}
